import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box, CircularProgress, FormControl, TextField
} from "@material-ui/core";
import { Alert, Autocomplete } from "@material-ui/lab";
import { useTranslation } from "react-i18next";
import userAuthenticationConfig from "../../../../utils/userAuthenticationConfig";
import DefaultButton from "../../../styles/materialButtonStyle";
import MaterialDialog from "../../../styles/materialDialog";
import {
  StyledDialogAction,
  StyledDialogContent,
  StyledDialogHead,
  StyledDialogWrapper
} from "../../../styles/styledDialog";

const TelegramManagerAttributesDialog = ({ manager, projectId, open, onClose, setNotification }) => {
  const { t } = useTranslation("telegramBotPage");
  const [connections, setConnections] = useState(null);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [attributes, setAttributes] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchConnections = () => {
    axios.get(`/api/telegram/connections?projectId=${projectId}`, userAuthenticationConfig()).then(response => {
      if (response.status === 200) {
        const conns = response.data["hydra:member"] || [];
        setConnections(conns);
        if (conns.length === 1) {
          setSelectedServiceId(conns[0].serviceId);
        }
      }
    }).catch(() => {
      setConnections([]);
    });
  };

  const fetchAttributes = (serviceId) => {
    setAttributes(null);
    axios.get(`/api/telegram/managers/${manager.id}/attributes?serviceId=${serviceId}`, userAuthenticationConfig()).then(response => {
      if (response.status === 200) {
        const attrs = response.data["hydra:member"] || [];
        setAttributes(attrs);
        const initial = {};
        attrs.forEach(a => { initial[a.name] = a.value; });
        setValues(initial);
      }
    }).catch(error => {
      const msg = error?.response?.data?.message || t("managers.errors.loadAttrs");
      setNotification({ visible: true, type: "error", message: msg });
      setAttributes([]);
    });
  };

  const handleSaveAll = () => {
    setSaving(true);
    Promise.all(
      attributes.map(attr =>
        axios.put(
          `/api/telegram/managers/${manager.id}/attributes`,
          { serviceId: selectedServiceId, name: attr.name, value: values[attr.name] || "" },
          userAuthenticationConfig()
        )
      )
    ).then(() => {
      setNotification({ visible: true, type: "success", message: t("managers.success.saved") });
    }).catch(error => {
      const msg = error?.response?.data?.message || t("managers.errors.save");
      setNotification({ visible: true, type: "error", message: msg });
    }).finally(() => {
      setSaving(false);
    });
  };

  useEffect(() => {
    if (open) {
      setConnections(null);
      setSelectedServiceId("");
      setAttributes(null);
      setValues({});
      fetchConnections();
    }
  }, [open, manager.id]);

  useEffect(() => {
    if (selectedServiceId) {
      fetchAttributes(selectedServiceId);
    }
  }, [selectedServiceId]);

  const uniqueServices = connections
    ? connections.filter((c, i, arr) => arr.findIndex(x => x.serviceId === c.serviceId) === i)
    : [];

  return (
    <MaterialDialog open={open}>
      <StyledDialogWrapper>
        <StyledDialogHead>
          {t("managers.dialog.attrsTitle", { name: manager.name })}
        </StyledDialogHead>
        <StyledDialogContent>
          {connections === null ? (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <FormControl fullWidth>
                <Autocomplete
                  options={uniqueServices}
                  value={uniqueServices.find(c => c.serviceId === selectedServiceId) || null}
                  onChange={(event, conn) => setSelectedServiceId(conn ? conn.serviceId : "")}
                  getOptionLabel={(option) => option.serviceName}
                  renderInput={(params) => (
                    <TextField {...params} label={t("managers.attributes.selectService")} />
                  )}
                  blurOnSelect
                  disableClearable
                />
              </FormControl>

              {selectedServiceId && (
                attributes === null ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress />
                  </Box>
                ) : attributes.length === 0 ? (
                  <Alert severity="info">{t("managers.attributes.empty")}</Alert>
                ) : (
                  <>
                    {attributes.map(attr => (
                      <TextField
                        key={attr.name}
                        label={attr.name}
                        value={values[attr.name] || ""}
                        onChange={e => {
                          const v = e.target.value;
                          setValues(prev => ({ ...prev, [attr.name]: v }));
                        }}
                        fullWidth
                      />
                    ))}
                  </>
                )
              )}
            </>
          )}
        </StyledDialogContent>
        <StyledDialogAction col="2">
          <DefaultButton onClick={onClose} color="danger">
            {t("managers.dialog.close")}
          </DefaultButton>
          <DefaultButton
            onClick={handleSaveAll}
            color="success"
            disabled={!attributes || attributes.length === 0 || saving}
          >
            {saving ? <CircularProgress size={18} /> : t("managers.dialog.save")}
          </DefaultButton>
        </StyledDialogAction>
      </StyledDialogWrapper>
    </MaterialDialog>
  );
};

export default TelegramManagerAttributesDialog;